import { SerializedEditorState } from "lexical"

import { htmlToNodes, markdownToNodes } from "./index"

type Part = { nodes?: SerializedEditorState; html?: string; markdown?: string }

// Define types for Lexical serialized nodes
interface SerializedLexicalNode {
  children?: SerializedLexicalNode[]
  direction: null | string
  format: string
  indent: number
  type: string
  version: number
  [key: string]: unknown // Allow additional properties for custom nodes
}

interface SerializedRootNode extends SerializedLexicalNode {
  type: "root"
  children: SerializedLexicalNode[]
}

type SerializedRoot = {
  root: SerializedRootNode
}

/**
 * Create a serialized empty paragraph node.
 */
function emptyParagraph(): SerializedLexicalNode {
  return {
    children: [],
    direction: null,
    format: "",
    indent: 0,
    type: "paragraph",
    version: 1,
  }
}

/**
 * Extract top-level children array from a SerializedEditorState.
 */
function getChildren(state: SerializedEditorState | undefined | null): SerializedLexicalNode[] {
  // We trust the upstream serialization shape produced by Lexical.
  const s = state as unknown as SerializedRoot | null | undefined
  return s?.root?.children ?? []
}

/**
 * Merge a list of Lexical parts (nodes / html / markdown) into a single SerializedEditorState.
 * - Preserves custom nodes when `nodes` are provided (no DOM round-trip).
 * - Uses DOM conversion for html/markdown.
 * - Appends directly to root. No reliance on selection.
 * - Adds a blank paragraph between parts when `withSeparators` is true.
 */
export function mergeLexicalContents(parts: Part[], withSeparators = true): SerializedEditorState {
  // Normalize + filter empties up front
  const items = parts.filter((p) => {
    const hasNodes = !!p.nodes
    const hasHtml = typeof p.html === "string" && p.html.trim() !== ""
    const hasMd = typeof p.markdown === "string" && p.markdown.trim() !== ""
    return hasNodes || hasHtml || hasMd
  })

  // Accumulate serialized children here to preserve custom node types
  const mergedChildren: SerializedLexicalNode[] = []

  items.forEach((p, idx) => {
    let stateForPart: SerializedEditorState | null = null

    if (p.nodes) {
      // Keep as-is to preserve custom nodes (e.g. RawHtmlNode)
      stateForPart = p.nodes
    } else if (p.html) {
      stateForPart = htmlToNodes(p.html)
    } else if (p.markdown) {
      stateForPart = markdownToNodes(p.markdown)
    }

    const children = getChildren(stateForPart)
    if (children.length > 0) {
      mergedChildren.push(...children)
    }

    // Deterministic separator between parts (but not after the last part)
    if (withSeparators && idx < items.length - 1) {
      // Avoid piling up multiple consecutive empty paragraphs
      const prev = mergedChildren[mergedChildren.length - 1]
      const needsSeparator =
        !prev || !(prev.type === "paragraph" && Array.isArray(prev.children) && prev.children.length === 0)
      if (needsSeparator) mergedChildren.push(emptyParagraph())
    }
  })

  if (mergedChildren.length === 0) {
    mergedChildren.push(emptyParagraph())
  }

  const merged: SerializedRoot = {
    root: {
      children: mergedChildren,
      direction: null,
      format: "",
      indent: 0,
      type: "root",
      version: 1,
    },
  }

  // Return as SerializedEditorState; Lexical will parse this just fine.
  return merged as unknown as SerializedEditorState
}
